import React, { useMemo } from 'react';
import { Form, Button, Card, DatePicker, InputNumber, Select, Table, Alert, Progress, Input, Tooltip } from 'antd';
import { CreateDealPaymentCommand, CreateUpdateDealUnitCommand, PaymentMethod } from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { formatCurrency } from 'utility/utility';
import dayjs from 'dayjs';
import moment from 'moment';
import styled from 'styled-components';

interface Props {
  units?: CreateUpdateDealUnitCommand[];
  value?: CreateDealPaymentCommand[];
  onChange: (payments: CreateDealPaymentCommand[]) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 24px;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 600;
`;

const RemainingValue = styled(SummaryValue)`
  color: #1890ff;
`;

const PaymentForm = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FullWidthFormItem = styled(Form.Item)`
  .ant-input-number {
    width: 100%;
  }
  .ant-picker {
    width: 100%;
  }
`;

const SummaryAndFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 24px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const PaymentPlan: React.FC<Props> = ({ units = [], value = [], onChange }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // Calculate total deal amount from selected units
  const totalDealAmount = units.reduce((total, unit) => {
    if (unit.negotiatedPricePerSqm) {
      const quantity = unit.quantity || 0;
      const squareMeters = unit.squareMeters || 0;
      return total + quantity * unit.negotiatedPricePerSqm * squareMeters;
    }

    return total + (unit.totalPrice || 0);
  }, 0);

  const totalScheduledAmount = value.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  const remainingAmount = totalDealAmount - totalScheduledAmount;
  const progressPercent =
    totalDealAmount > 0 ? Math.min(100, Math.ceil((totalScheduledAmount / totalDealAmount) * 100 * 100) / 100) : 0;

  const handleAddPayment = (values: any) => {
    const newPayment: CreateDealPaymentCommand = {
      amount: values.amount,
      dueDate: values.dueDate.toISOString(),
      paymentMethod: values.paymentMethod,
      notes: values.notes,
    };

    onChange([...value, newPayment]);
    form.resetFields();
  };

  const handleDeletePayment = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const columns = [
    {
      title: t('payments:due-date', 'Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: t('payments:amount', 'Amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method: any) => {
        const methods: Record<PaymentMethod, { icon: string; label: string }> = {
          [PaymentMethod.Cash]: { icon: 'dollar-sign', label: 'Cash' },
          [PaymentMethod.Bank]: { icon: 'credit-card', label: 'Bank Transfer' },
          [PaymentMethod.Crypto]: { icon: 'shield', label: 'Crypto' },
          [PaymentMethod.Other]: { icon: 'help-circle', label: 'Other' },
        };
        const { icon, label } = methods[method as PaymentMethod];
        return (
          <IconWrapper>
            <FeatherIcon icon={icon} size={16} />
            <span>{label}</span>
          </IconWrapper>
        );
      },
    },
    {
      title: t('payments:notes', 'Notes'),
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => (
        <Tooltip title={notes}>
          <span>{notes?.length > 30 ? `${notes.substring(0, 30)}...` : notes}</span>
        </Tooltip>
      ),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'actions',
      render: (_: any, _record: any, index: number) => (
        <Button
          type="link"
          danger
          icon={<FeatherIcon icon="trash-2" size={16} />}
          onClick={() => handleDeletePayment(index)}
        />
      ),
    },
  ];

  return (
    <Container>
      <SummaryAndFormGrid>
        {/* Add Payment Card */}
        <Card
          title={
            <IconWrapper>
              <FeatherIcon icon="plus-circle" size={16} />
              <span>{t('payments-plan:schedule-payment', 'Schedule Payment')}</span>
            </IconWrapper>
          }
        >
          <Form form={form} layout="vertical" onFinish={handleAddPayment}>
            <PaymentForm>
              <Form.Item
                name="amount"
                label={t('payments:amount', 'Amount')}
                rules={[
                  { required: true },
                  {
                    validator: (_, value) => {
                      if (value > remainingAmount) {
                        return Promise.reject(t('payments-plan:add-payment', 'Amount exceeds remaining balance'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  className="w-full"
                  min={0}
                  max={remainingAmount}
                  style={{ width: '100%' }}
                  formatter={value => formatCurrency(value || 0)}
                  parser={value => parseFloat(value!.replace(/[^\d.]/g, ''))}
                />
              </Form.Item>

              <FullWidthFormItem name="paymentMethod" label="Payment Method" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value={0}>
                    <IconWrapper>
                      <FeatherIcon icon="dollar-sign" size={16} />
                      {t('payments-plan:payment-method-cash', 'Cash')}
                    </IconWrapper>
                  </Select.Option>
                  <Select.Option value={1}>
                    <IconWrapper>
                      <FeatherIcon icon="credit-card" size={16} />
                      {t('payments-plan:payment-method-bank', 'Bank Transfer')}
                    </IconWrapper>
                  </Select.Option>
                  <Select.Option value={2}>
                    <IconWrapper>
                      <FeatherIcon icon="shield" size={16} />
                      {t('payments-plan:payment-method-crypto', 'Crypto')}
                    </IconWrapper>
                  </Select.Option>
                </Select>
              </FullWidthFormItem>

              <Form.Item
                name="dueDate"
                getValueProps={i => ({ value: moment(i) })}
                label={t('payment-plan.due-date', 'Due Date')}
                rules={[
                  {
                    required: true,
                    message: t('payment-plan.select-due-date', 'Please select a due date'),
                  },
                ]}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  placeholder={t('payment-plan.due-date', 'Select due date')}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <FullWidthFormItem name="notes" label={t('payments-plan:notes', 'Notes')}>
                <Input.TextArea placeholder={t('payments-plan:notes-placeholder', 'Enter payment notes')} rows={1} />
              </FullWidthFormItem>
            </PaymentForm>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                icon={<FeatherIcon icon="plus" size={16} />}
                htmlType="submit"
                block
                disabled={remainingAmount <= 0}
              >
                {t('payments-plan:add-payment', 'Add Payment')}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* Summary Card */}
        <Card>
          <SummaryGrid>
            <SummaryItem>
              <SummaryLabel>{t('payments-plan:total-amount', 'Total Amount')}</SummaryLabel>
              <SummaryValue>{formatCurrency(totalDealAmount)}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>{t('payments-plan:total-scheduled', 'Scheduled')}</SummaryLabel>
              <SummaryValue>{formatCurrency(totalScheduledAmount)}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>{t('payments-plan:total-remaining', 'Remaining')}</SummaryLabel>
              <RemainingValue>{formatCurrency(remainingAmount)}</RemainingValue>
            </SummaryItem>
          </SummaryGrid>
          <Progress percent={progressPercent} status={progressPercent === 100 ? 'success' : 'active'} strokeWidth={8} />
        </Card>
      </SummaryAndFormGrid>

      {remainingAmount > 0 ? (
        <Alert
          type="warning"
          message={t(
            'payments-plan:payments-scheduled-required-validation',
            'Please schedule all payments to continue',
          )}
          icon={<FeatherIcon icon="alert-triangle" size={16} />}
        />
      ) : remainingAmount < 0 ? (
        <Alert
          type="error"
          message={t('payments-plan:payments-scheduled-exceed-total-amount', 'Scheduled payments exceed total amount')}
          icon={<FeatherIcon icon="alert-circle" size={16} />}
        />
      ) : value.length > 0 ? (
        <Alert
          type="success"
          message={t('payments-plan:payments-scheduled', 'All payments scheduled')}
          icon={<FeatherIcon icon="check-circle" size={16} />}
        />
      ) : null}

      <Card>
        <Table dataSource={value} columns={columns} pagination={false} rowKey={(_, index) => index!.toString()} />
      </Card>
    </Container>
  );
};

export default PaymentPlan;
